import { defineStore } from 'pinia'
import api from '../services/api'
import { useAuthStore } from './auth-store'
import type { Project, CreateProjectRequest, UpdateProjectRequest } from '../pages/projects/types'

export const useProjectsStore = defineStore('projects', {
  state: () => {
    return {
      items: [] as Project[],
      isLoading: false,
      error: null as Error | null,
    }
  },

  getters: {
    hasProjects: (state) => state.items.length > 0,
  },

  actions: {
    async getAll() {
      this.isLoading = true
      this.error = null

      try {
        const authStore = useAuthStore()
        if (!authStore.token) {
          throw new Error('No authentication token')
        }

        const response = await fetch(api.projects(), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authStore.token}`,
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            authStore.clearAuth()
            throw new Error(response.status === 403 ? 'Forbidden' : 'Unauthorized')
          }
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to fetch projects')
        }

        const data = await response.json()
        this.items = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []
        return this.items
      } catch (error) {
        this.error = error instanceof Error ? error : new Error('Unknown error')
        throw this.error
      } finally {
        this.isLoading = false
      }
    },

    async getById(id: string) {
      this.isLoading = true
      this.error = null

      try {
        const authStore = useAuthStore()
        if (!authStore.token) {
          throw new Error('No authentication token')
        }

        const response = await fetch(api.project(id), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authStore.token}`,
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            authStore.clearAuth()
            throw new Error(response.status === 403 ? 'Forbidden' : 'Unauthorized')
          }
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to fetch project')
        }

        const data = await response.json()
        const project = data.data || data
        return project as Project
      } catch (error) {
        this.error = error instanceof Error ? error : new Error('Unknown error')
        throw this.error
      } finally {
        this.isLoading = false
      }
    },

    async create(projectData: CreateProjectRequest) {
      this.isLoading = true
      this.error = null

      try {
        const authStore = useAuthStore()
        if (!authStore.token) {
          throw new Error('No authentication token')
        }

        const response = await fetch(api.projects(), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authStore.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(projectData),
        })

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            authStore.clearAuth()
            throw new Error(response.status === 403 ? 'Forbidden' : 'Unauthorized')
          }
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to create project')
        }

        const data = await response.json()
        const project = data.data || data
        this.items.push(project as Project)
        return project as Project
      } catch (error) {
        this.error = error instanceof Error ? error : new Error('Unknown error')
        throw this.error
      } finally {
        this.isLoading = false
      }
    },

    async update(id: string, projectData: UpdateProjectRequest) {
      this.isLoading = true
      this.error = null

      try {
        const authStore = useAuthStore()
        if (!authStore.token) {
          throw new Error('No authentication token')
        }

        const response = await fetch(api.project(id), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authStore.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(projectData),
        })

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            authStore.clearAuth()
            throw new Error(response.status === 403 ? 'Forbidden' : 'Unauthorized')
          }
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to update project')
        }

        const data = await response.json()
        const updatedProject = data.data || data
        
        // Update the project in the store
        const index = this.items.findIndex((p) => p.id === id)
        if (index !== -1) {
          this.items[index] = updatedProject as Project
        }
        
        return updatedProject as Project
      } catch (error) {
        this.error = error instanceof Error ? error : new Error('Unknown error')
        throw this.error
      } finally {
        this.isLoading = false
      }
    },

    async remove(id: string, targetProjectId: string) {
      this.isLoading = true
      this.error = null

      try {
        const authStore = useAuthStore()
        if (!authStore.token) {
          throw new Error('No authentication token')
        }

        const response = await fetch(api.project(id), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authStore.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            targetProjectId: targetProjectId,
          }),
        })

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            authStore.clearAuth()
            throw new Error(response.status === 403 ? 'Forbidden' : 'Unauthorized')
          }
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to delete project')
        }

        this.items = this.items.filter((p) => p.id !== id)
      } catch (error) {
        this.error = error instanceof Error ? error : new Error('Unknown error')
        throw this.error
      } finally {
        this.isLoading = false
      }
    },
  },
})

